import {
	Card,
	CardBody,
	CardHeader,
	CardHeaderActions,
	CardHeading,
	Icon,
	IconButton,
	IconLinkButton,
	TextAnchor,
	useEditDrawer,
} from "@dgs/core";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { EditApplicationDrawer } from "../../../applications/EditApplicationDrawer";
import { ApplicationDetailResource, ApplicationType, EventDetailResource } from "../../../shared/types";
import { HostingDuration } from "../HostingDuration";

interface Props {
	event: EventDetailResource;
	application: ApplicationDetailResource;
}

const Wrapper = styled(Card)`
	min-height: 300px;
	display: flex;
	flex-direction: column;
`;

const ApplicationCardBody = styled(CardBody)`
	display: flex;
	flex-direction: column;
	flex: 1;
	overflow: auto;
	gap: ${({ theme }) => theme.spacing(5)};
	justify-content: center;
`;

const ApplicationCardHeader = styled(CardHeader)`
	display: grid;
	grid-template-columns: 1fr;
	gap: ${({ theme }) => theme.spacing(2)};

	${({ theme }) => theme.breakpoints.desktop} {
		grid-template-columns: 1fr max-content;
	}
`;

export const ApplicationCard: FC<Props> = ({ event, application }) => {
	const { t } = useTranslation();
	const { editEntityId, handleOpen, ...editProps } = useEditDrawer();

	const heading = useMemo(() => {
		switch (application.type) {
			case ApplicationType.PARTICIPANT_MANAGEMENT:
				return "Participant Management";
			case ApplicationType.KRONOS:
				return "KRONOS";
			case ApplicationType.DEEP: {
				if (application.configuration.enableFrontend && !application.configuration.enableApp) {
					return "DEEP";
				}
				if (!application.configuration.enableFrontend && application.configuration.enableApp) {
					return "APP";
				}

				return "DEEP & APP";
			}
		}
	}, [application.configuration.enableApp, application.configuration.enableFrontend, application.type]);

	return (
		<>
			<Wrapper>
				<ApplicationCardHeader>
					<div>
						<CardHeading>{`${heading} - ${application.projectNumber}`}</CardHeading>
						{application.deletionDate && (
							<HostingDuration deletionDate={application.deletionDate} eventEnd={event.end} />
						)}
					</div>
					<CardHeaderActions>
						<IconButton
							onClick={() => handleOpen(application.id)}
							size="small"
							title={t("Edit application")}
							icon="edit"
						/>
						<IconLinkButton
							to={`/events/active/${event.id}/details/applications/${application.id}/users`}
							size="small"
							icon="group"
							title={t("Open application users")}
						/>
					</CardHeaderActions>
				</ApplicationCardHeader>
				<ApplicationCardBody>
					{application.domainData.nominationManagerUrl && (
						<TextAnchor
							color="primary"
							href={application.domainData.nominationManagerUrl}
							prefix={<Icon $icon="external_link" />}
						>
							{t("Open nomination manager")}
						</TextAnchor>
					)}
					{application.domainData.frontendUrl && (
						<TextAnchor
							color="primary"
							href={application.domainData.frontendUrl}
							prefix={<Icon $icon="external_link" />}
						>
							{t("Open frontend")}
						</TextAnchor>
					)}
					<TextAnchor color="primary" href={application.domainData.adminUrl} prefix={<Icon $icon="external_link" />}>
						{t("Open admin")}
					</TextAnchor>
				</ApplicationCardBody>
			</Wrapper>
			{editEntityId && (
				<EditApplicationDrawer
					entityId={editEntityId}
					applicationType={application.type}
					eventId={event.id}
					{...editProps}
				/>
			)}
		</>
	);
};
